import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
} from '@floating-ui/react';
import { ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';

interface TooltippedTextProps {
  tooltipContent?: ReactNode;
  children: ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  offsetValue?: number;
  tooltipClassName?: string;
  spanClassName?: string;
}

export const TooltippedText = ({
  tooltipContent,
  children,
  placement = 'top',
  offsetValue = 8,
  tooltipClassName = '',
  spanClassName = '',
}: TooltippedTextProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(offsetValue),
      flip({
        fallbackAxisSideDirection: 'start',
      }),
      shift(),
    ],
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  if (!tooltipContent) {
    return <>{children}</>;
  }

  return (
    <>
      <span ref={refs.setReference} {...getReferenceProps()} className={spanClassName}>
        {children}
      </span>
      <FloatingPortal>
        {isOpen && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={twMerge(
              'tw:z-[9999] tw:px-2 tw:py-1 tw:text-sm tw:font-medium tw:text-white tw:bg-brand-green-0 tw:rounded tw:shadow-sm tw:max-w-xs tw:break-words',
              tooltipClassName
            )}
          >
            {tooltipContent}
          </div>
        )}
      </FloatingPortal>
    </>
  );
};
